import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import React, { useEffect, useRef, useState } from 'react'
import { useDebounced } from 'src/hooks'
import { useNormalizeBackendError } from 'src/utils/error.utils'

export interface UseAsyncTextInputOptions {
  /**
   * The current value from external state (controlled value)
   */
  value?: string
  /**
   * callback that handles the async operation when the input value changes
   */
  onValueChange: (value: string) => Promise<void> | void
  /**
   * Debounce delay in milliseconds
   * @default 500
   */
  debounceDelay?: number
}

export interface UseAsyncTextInputReturn {
  /**
   * local value to reflect user changes
   */
  value: string
  loadingState: LoadingState
  errorText: string | undefined
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
}

/**
 * Hook that manages async text input with debouncing, loading states, and error handling.
 * Abstracts the common pattern of local state + debounced async operations.
 */
export const useAsyncTextInput = ({
  value = '',
  onValueChange,
  debounceDelay = 500,
}: UseAsyncTextInputOptions): UseAsyncTextInputReturn => {
  const normalizeError = useNormalizeBackendError()

  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState<string>(value)
  const [errorText, setErrorText] = useState<string | undefined>()
  const [loadingState, setLoadingState] = useState<LoadingState>('idle')

  // Tracks the last value the server has confirmed (or the initial controlled
  // value). Used both to distinguish a server-echoed `value` prop update
  // (which should NOT clobber in-flight user edits) from a genuinely external
  // reset (which should), and as the rollback target on a failed commit.
  const lastCommittedValueRef = useRef<string>(value)

  // Handle async value change with error handling
  const handleAsyncValueChange = async (newValue: string) => {
    const result = onValueChange(newValue)

    // Check if onValueChange returns a Promise
    if (
      result &&
      typeof result === 'object' &&
      typeof result.then === 'function'
    ) {
      setLoadingState('pending')

      // TODO: consider abstracting away disabled behaviour in the hook as well
      await result
        .then(() => {
          lastCommittedValueRef.current = newValue
          setLoadingState('success')
        })
        .catch((error) => {
          const e = normalizeError(error)
          setLoadingState('error')
          setErrorText(e?.error)
          // Roll back to the last value the server has confirmed — but only
          // if the textbox still holds the value that just failed. If the
          // user has typed past it, leave their newer input alone.
          setLocalValue((current) =>
            current === newValue ? lastCommittedValueRef.current : current,
          )
        })
    } else {
      lastCommittedValueRef.current = newValue
    }
  }

  // Debounce the async handler
  const { debouncedF: debouncedHandleAsyncValueChange } = useDebounced(
    handleAsyncValueChange,
    debounceDelay,
  )

  // Handle input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value

    // Clear any previous error — both the message and the error indicator —
    // so the user isn't typing a correction with the red error UI still on.
    // Preserve 'pending' and 'success' across keystrokes so the indicator
    // does not flicker.
    if (errorText !== undefined) {
      setErrorText(undefined)
    }
    if (loadingState === 'error') {
      setLoadingState('idle')
    }

    // Update local state immediately for responsive UI
    setLocalValue(newValue)

    // Debounce the async operation
    debouncedHandleAsyncValueChange(newValue)
  }

  // Sync local value with external value changes — but only when the change is
  // genuinely external. If the new `value` matches what we last committed, it
  // is the server echoing our own write back at us, and resetting local state
  // would erase any characters the user has typed since the commit.
  useEffect(() => {
    if (value === lastCommittedValueRef.current) return
    lastCommittedValueRef.current = value
    setLocalValue(value)
  }, [value])

  return {
    value: localValue,
    loadingState,
    errorText,
    onChange: handleInputChange,
  }
}
